import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../auth/useAuth';
import { useMessages } from '../hooks/useMessages';
import { useTypingStatus } from '../hooks/useTypingStatus';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { ThreadList } from '../components/message/ThreadList';
import { MessageList } from '../components/message/MessageList';
import { MessageInput } from '../components/message/MessageInput';
import { NewConversationModal } from '../components/message/NewConversationModal';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../i18n/useLanguage';
import type { SendMessageRequest, MessageThread } from '../types/message';
import type { UserSearchResult } from '../types/user';
import './MessagesPage.css';

const MOBILE_LAYOUT_MQ = '(max-width: 968px)';

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || name[0]?.toUpperCase() || '?';

interface MessagesChatHeaderProps {
  title: string;
  initials: string;
  titleSuffix?: string;
  onBack?: () => void;
}

const MessagesChatHeader: React.FC<MessagesChatHeaderProps> = ({
  title,
  initials,
  titleSuffix,
  onBack,
}) => {
  const { t } = useLanguage();
  return (
    <div className="messages-chat-header">
      {onBack && (
        <button
          type="button"
          className="messages-chat-back"
          onClick={onBack}
          aria-label={t('messages.chat.backAria')}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      <div className="messages-chat-header-info">
        <div className="messages-chat-header-avatar">{initials}</div>
        <div className="messages-chat-header-details">
          <h3 className="messages-chat-header-name">
            {title}
            {titleSuffix}
          </h3>
        </div>
      </div>
    </div>
  );
};

interface ThreadChatHeaderProps {
  thread: MessageThread | undefined;
  isLoading?: boolean;
  onBack?: () => void;
}

const ThreadChatHeader: React.FC<ThreadChatHeaderProps> = ({ thread, isLoading, onBack }) => {
  const { t } = useLanguage();
  if (!thread) {
    return (
      <MessagesChatHeader
        title={t('messages.chat.loading')}
        initials="?"
        onBack={onBack}
      />
    );
  }
  return (
    <MessagesChatHeader
      title={thread.otherParticipantUsername}
      initials={getInitials(thread.otherParticipantUsername)}
      titleSuffix={isLoading ? t('messages.chat.loadingSuffix') : undefined}
      onBack={onBack}
    />
  );
};

export const MessagesPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isMobileLayout = useMatchMedia(MOBILE_LAYOUT_MQ);
  const {
    threads,
    messages,
    currentThreadId,
    unreadCount,
    isLoading,
    error,
    hasMoreMessages,
    loadMoreMessages,
    sendMessage: sendMessageApi,
    selectThread,
    deleteThread,
    refresh,
  } = useMessages();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const { typingUsers, sendTypingSignal } = useTypingStatus(currentThreadId);

  useEffect(() => {
    const userId = searchParams.get('userId');
    const username = searchParams.get('username');
    if (userId && username) {
      const existingThread = threads.find(th => th.otherParticipantId === userId);
      if (existingThread) {
        void selectThread(existingThread.id);
      } else {
        setSelectedUser({ id: userId, username });
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, threads, selectThread]);

  const mobileChatOpen =
    isMobileLayout && (!!currentThreadId || !!selectedUser);

  const handleBackToInbox = useCallback(() => {
    void selectThread(null);
    setSelectedUser(null);
  }, [selectThread]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleOpenNewConversation = () => {
    setShowNewConversationModal(true);
  };

  const handleCloseNewConversation = () => {
    setShowNewConversationModal(false);
  };

  const handleSelectUserFromModal = async (user: UserSearchResult) => {
    setSelectedUser(user);
    setShowNewConversationModal(false);
  };

  const handleSelectThread = (threadId: string) => {
    void selectThread(threadId);
  };

  const handleSendMessage = async (content: string) => {
    if (!currentThreadId && !selectedUser) {
      throw new Error('Please select a thread or search for a user to message');
    }

    let targetRecipientId: string | undefined;
    if (currentThreadId) {
      const currentThread = threads.find(th => th.id === currentThreadId);
      if (currentThread) {
        targetRecipientId = currentThread.otherParticipantId;
      }
    } else if (selectedUser) {
      targetRecipientId = selectedUser.id;
    }

    if (!targetRecipientId) {
      throw new Error('Recipient ID is required');
    }

    const request: SendMessageRequest = {
      content,
      recipientId: targetRecipientId,
    };

    const newMessage = await sendMessageApi(request);

    if (!currentThreadId) {
      await refresh();
      if (newMessage.threadId) {
        await selectThread(newMessage.threadId);
      }
      setSelectedUser(null);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const chatBackHandler = mobileChatOpen ? handleBackToInbox : undefined;

  return (
    <div className="messages-page">
      {error && <ErrorMessage message={error} />}

      <div
        className={clsx(
          'messages-page-content',
          isMobileLayout &&
            (mobileChatOpen
              ? 'messages-page-content--mobile-chat'
              : 'messages-page-content--mobile-inbox')
        )}
      >
        <div className="messages-sidebar">
          <div className="messages-sidebar-header">
            <h2 className="messages-sidebar-title">{t('messages.sidebar.title')}</h2>
            <div className="messages-sidebar-header-actions">
              {unreadCount > 0 && (
                <span className="messages-unread-badge">{unreadCount}</span>
              )}
              <button
                className="messages-sidebar-new-button"
                onClick={handleOpenNewConversation}
                title={t('messages.sidebar.newTooltip')}
                aria-label={t('messages.sidebar.newAria')}
                type="button"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>
          <ThreadList
            threads={threads}
            currentThreadId={currentThreadId}
            onSelectThread={handleSelectThread}
            onDeleteThread={deleteThread}
            isLoading={isLoading}
          />
        </div>

        <div className="messages-main">
          {currentThreadId ? (
            <>
              <ThreadChatHeader
                thread={threads.find(th => th.id === currentThreadId)}
                isLoading={isLoading}
                onBack={chatBackHandler}
              />
              <MessageList
                messages={messages}
                isLoading={isLoading}
                onLoadMore={hasMoreMessages ? loadMoreMessages : undefined}
                hasMore={hasMoreMessages}
                typingUsers={typingUsers}
              />
              <MessageInput
                onSendMessage={handleSendMessage}
                onTyping={sendTypingSignal}
                disabled={isLoading}
              />
            </>
          ) : selectedUser ? (
            <>
              <MessagesChatHeader
                title={selectedUser.username}
                initials={
                  selectedUser.username
                    .split(' ')
                    .map(word => word[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || selectedUser.username[0]?.toUpperCase() || '?'
                }
                onBack={chatBackHandler}
              />
              <div className="message-list">
                <div className="message-list-empty">
                  <p>{t('messages.startWithUser').replace('{username}', selectedUser.username)}</p>
                </div>
              </div>
              <MessageInput
                onSendMessage={handleSendMessage}
                disabled={isLoading}
                placeholder={t('messages.input.placeholderToUser').replace(
                  '{username}',
                  selectedUser.username
                )}
              />
            </>
          ) : (
            <>
              <div className="messages-empty-state">
                <div className="messages-empty-state-icon">
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <line x1="9" y1="10" x2="15" y2="10" />
                    <line x1="9" y1="14" x2="13" y2="14" />
                  </svg>
                </div>
                <p>{t('messages.empty.selectThread')}</p>
                <p style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--spacing-xs)' }}>
                  {t('messages.empty.hintPlus')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={handleCloseNewConversation}
        onSelectUser={handleSelectUserFromModal}
      />
    </div>
  );
};
